import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { X, ArrowRight, Loader2 } from 'lucide-react';

interface ConvertPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPoints: number;
  pointsPerStamp?: number;
  franchiseId: string;
  branchId?: string;
}

export const ConvertPointsModal: React.FC<ConvertPointsModalProps> = ({
  isOpen,
  onClose,
  currentPoints,
  pointsPerStamp: propPointsPerStamp,
  franchiseId,
  branchId
}) => {
  const { t } = useTranslation();
  const [stampsRequested, setStampsRequested] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [pointsPerStamp, setPointsPerStamp] = useState(propPointsPerStamp || 50);

  const pointsCost = stampsRequested * pointsPerStamp;
  const remainingPoints = currentPoints - pointsCost;
  const maxStamps = Math.floor(currentPoints / pointsPerStamp);

  // Load configuration from Firestore
  useEffect(() => {
    if (!isOpen || !franchiseId) return;

    const loadConfig = async () => {
      try {
        setLoadingConfig(true);
        const configDoc = await getDoc(doc(db, 'loyalty_configs', franchiseId));

        if (configDoc.exists()) {
          const configData = configDoc.data();
          const stampsConfig = configData?.stamps || {};
          const conversionConfig = stampsConfig.pointsConversion || {};

          // Use config from Firestore, fallback to prop, then default
          const configuredPointsPerStamp = conversionConfig.pointsPerStamp
            || configData?.pointsPerStamp
            || propPointsPerStamp
            || 50;

          setPointsPerStamp(configuredPointsPerStamp);
        } else {
          // Use prop or default if no config exists
          setPointsPerStamp(propPointsPerStamp || 50);
        }
      } catch (error) {
        console.error('Error loading loyalty config:', error);
        // Fallback to prop or default on error
        setPointsPerStamp(propPointsPerStamp || 50);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadConfig();
  }, [isOpen, franchiseId, propPointsPerStamp]);

  useEffect(() => {
    if (maxStamps > 0 && stampsRequested > maxStamps) {
      setStampsRequested(maxStamps);
    }
  }, [maxStamps]);

  const handleConvert = async () => {
    if (pointsCost > currentPoints) {
      toast.error(t('loyalty.conversion.notEnoughPoints'));
      return;
    }

    setLoading(true);

    try {
      const convertFn = httpsCallable(functions, 'convertPointsToStamps');
      const result = await convertFn({
        franchiseId,
        branchId,
        stampsRequested
      });

      if (result.data) {
        toast.success(t('loyalty.conversion.requestSent'));
        onClose();
      }
    } catch (error: any) {
      console.error('Error converting points:', error);
      toast.error(error.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-2xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-hairline">
          <h3 className="text-xl font-bold text-ink">
            {t('loyalty.conversion.titleToStamps')}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-ink-muted" />
          </button>
        </div>

        {/* Loading state */}
        {loadingConfig ? (
          <div className="p-6 flex flex-col items-center justify-center space-y-4">
            <Loader2 className="w-8 h-8 text-accent animate-spin" />
            <p className="text-sm text-ink-muted">{t('loyalty.conversion.loadingConfig')}</p>
          </div>
        ) : (
          <>
            {/* Content */}
            <div className="p-6 space-y-6">
          {/* Current balance */}
          <div className="rounded-lg p-4 border border-accent/30">
            <p className="text-sm text-ink-muted">{t('loyalty.conversion.currentPoints')}</p>
            <p className="text-3xl font-bold text-accent">{currentPoints}</p>
          </div>

          {/* Conversion rate */}
          <div className="flex items-center justify-center gap-3 py-2">
            <div className="text-center">
              <p className="text-2xl font-bold text-accent">{pointsPerStamp}</p>
              <p className="text-xs text-ink-subtle">{t('loyalty.points.title').toLowerCase()}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-ink-subtle" />
            <div className="text-center">
              <p className="text-2xl font-bold text-accent">1</p>
              <p className="text-xs text-ink-subtle">{t('loyalty.card.stamp')}</p>
            </div>
          </div>

          {/* Stamps selector */}
          <div>
            <label className="block text-sm font-medium text-ink-muted mb-2">
              {t('loyalty.conversion.howMany')}
            </label>
            <div className="flex items-center gap-4 justify-center">
              <button
                onClick={() => setStampsRequested(Math.max(1, stampsRequested - 1))}
                disabled={stampsRequested <= 1}
                className="w-10 h-10 rounded-lg bg-surface-2 hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-ink"
              >
                -
              </button>
              <input
                type="number"
                value={stampsRequested}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= maxStamps) {
                    setStampsRequested(val);
                  }
                }}
                min="1"
                max={maxStamps}
                className="w-20 text-center text-2xl font-bold border-2 border-hairline rounded-lg py-2 bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent"
              />
              <button
                onClick={() => setStampsRequested(Math.min(maxStamps, stampsRequested + 1))}
                disabled={stampsRequested >= maxStamps}
                className="w-10 h-10 rounded-lg bg-surface-2 hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-ink"
              >
                +
              </button>
            </div>
            <p className="text-xs text-ink-subtle mt-2 text-center">
              {t('loyalty.conversion.maxStamps', { max: maxStamps })}
            </p>
          </div>

          {/* Calculation */}
          <div className="bg-surface-2 rounded-lg p-4 space-y-2 border border-hairline">
            <div className="flex justify-between text-sm">
              <span className="text-ink-muted">{t('loyalty.conversion.cost')}</span>
              <span className="font-semibold text-ink">-{pointsCost} {t('loyalty.points.title').toLowerCase()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-ink-muted">{t('loyalty.conversion.youWillReceive')}</span>
              <span className="font-semibold text-accent">+{stampsRequested} {t('loyalty.card.stamps')}</span>
            </div>
            <div className="border-t border-hairline pt-2 flex justify-between">
              <span className="text-sm font-medium text-ink-muted">{t('loyalty.conversion.remaining')}</span>
              <span className={`font-bold ${
                remainingPoints < 0 ? 'text-danger' : 'text-success'
              }`}>
                {remainingPoints}
              </span>
            </div>
          </div>

          {/* Warning */}
          {remainingPoints < 0 && (
            <div className="bg-danger/10 border border-danger/30 rounded-lg p-3">
              <p className="text-sm text-danger">
                {t('loyalty.conversion.notEnoughForConversion')}
              </p>
            </div>
          )}

          {/* Info */}
          <div className="bg-info/10 border border-info/30 rounded-lg p-3">
            <p className="text-xs text-info">
              {t('loyalty.conversion.requestInfo')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-hairline p-6 flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-3 px-4 border border-hairline text-ink-muted rounded-lg hover:bg-surface-2 disabled:opacity-50 font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleConvert}
            disabled={loading || remainingPoints < 0 || stampsRequested < 1}
            className="flex-1 py-3 px-4 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg"
          >
            {loading ? t('common.sending') : t('loyalty.conversion.requestConversion')}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
};
